import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { formatMoney, toCents } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const EstimateDialog = ({ open, onOpenChange }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [companyId, setCompanyId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [monto, setMonto] = useState("");
  const [brand, setBrand] = useState("bb");

  const { data: companies } = useQuery({
    queryKey: ["companies-lite"], enabled: open,
    queryFn: async () => (await api.get("/companies", { params: { page_size: 200 } })).data.items,
  });
  const { data: properties } = useQuery({
    queryKey: ["properties-by-company", companyId], enabled: open && Boolean(companyId),
    queryFn: async () => (await api.get("/properties", { params: { company_id: companyId, page_size: 200 } })).data.items,
  });

  const reset = () => { setCompanyId(""); setPropertyId(""); setDescripcion(""); setMonto(""); };

  const mutation = useMutation({
    mutationFn: () => api.post("/estimates", {
      brand, company_id: companyId, property_id: propertyId, descripcion, monto_cents: toCents(monto), estado: "enviado",
    }),
    onSuccess: () => {
      toast.success(t("estimates.created"));
      qc.invalidateQueries({ queryKey: ["estimates"] });
      reset();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="estimate-dialog" className="max-w-lg rounded-[10px]">
        <DialogHeader><DialogTitle>{t("estimates.new")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12.5px]">{t("estimates.company")}</Label>
              <Select value={companyId} onValueChange={(v) => { setCompanyId(v); setPropertyId(""); }}>
                <SelectTrigger data-testid="estimate-company-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[12.5px]">{t("estimates.building")}</Label>
              <Select value={propertyId} onValueChange={setPropertyId} disabled={!companyId}>
                <SelectTrigger data-testid="estimate-property-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(properties || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[12.5px]">{t("estimates.description")}</Label>
            <Textarea data-testid="estimate-description-input" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12.5px]">{t("estimates.amount")}</Label>
              <Input data-testid="estimate-amount-input" value={monto} onChange={(e) => setMonto(e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <Label className="text-[12.5px]">Marca</Label>
              <Select value={brand} onValueChange={setBrand}>
                <SelectTrigger data-testid="estimate-brand-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="bb">B&B</SelectItem><SelectItem value="betos">Betos</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="estimate-submit-button" className="bg-brand hover:bg-brand-hover"
            disabled={!companyId || !propertyId || !descripcion || toCents(monto) <= 0 || mutation.isPending}
            onClick={() => mutation.mutate()}>
            {t("estimates.new")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
